import { postSignupPath } from '../utils/postSignupPath.js';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { resolveLocale } from '../i18n';
import { toast } from 'react-hot-toast';
import useAuth from '../../context/auth.context.jsx';
import userService from '../services/userService';
import { peekPendingReferral, clearPendingReferral } from '../utils/referral.js';
import Sheet from './common/sheet.jsx';
import Button from './common/button.jsx';
import Input from './common/input.jsx';
import { SocialSignupConsentSheet, hasSignupConsent } from './signupConsent.jsx';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
// iOS needs its own OAuth client (separate from the Web one above) — Android
// and web work fine on webClientId alone.
const IOS_CLIENT_ID = import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID;

// Google refuses OAuth inside embedded webviews, so the GIS script below is
// web-only. The Capacitor build signs in through Android's own account picker
// and hands back an ID token with the same web-client audience the backend
// already verifies — same credential, different door.
const IS_NATIVE = Capacitor.isNativePlatform();

// Google's own SDK chrome (the button's accessible title, its dialogs) takes
// its language from the script's `hl` query param at load time — the
// `locale` option passed to renderButton() only steers part of the button,
// not all of it. Loading the script once, unparametrized, in index.html
// left it stuck on whatever locale Google negotiated on that first load
// (the visitor's Google account/browser default), ignoring our own i18n
// language entirely. So this loads (and reloads, on language change) the
// script itself with `hl` set, tearing down any prior instance first since
// GIS does not support being reinitialized in place.
let gsiLoadPromise = null;
let gsiLoadedHl = null;

function loadGsi(hl) {
    if (gsiLoadedHl === hl && gsiLoadPromise) return gsiLoadPromise;
    document.querySelectorAll('script[data-gsi-client]').forEach(s => s.remove());
    if (window.google?.accounts) delete window.google.accounts;
    gsiLoadedHl = hl;
    gsiLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://accounts.google.com/gsi/client?hl=${hl}`;
        script.async = true;
        script.defer = true;
        script.dataset.gsiClient = 'true';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
    return gsiLoadPromise;
}

/** Official GIS button. Renders nothing when VITE_GOOGLE_CLIENT_ID is not set. */
function WebGoogleButton({ onCredential, text = 'signin_with' }) {
    const ref = useRef(null);
    const { i18n } = useTranslation();
    const locale = resolveLocale(i18n.resolvedLanguage || i18n.language);

    useEffect(() => {
        if (!CLIENT_ID || !ref.current) return;
        let cancelled = false;

        loadGsi(locale).then(() => {
            if (cancelled || !ref.current) return;
            window.google.accounts.id.initialize({
                client_id: CLIENT_ID,
                callback: (response) => onCredential(response.credential),
            });
            window.google.accounts.id.renderButton(ref.current, {
                theme: 'outline',
                size: 'large',
                shape: 'pill',
                text,
                locale,
                width: 300,
            });
        }).catch(() => {});

        return () => { cancelled = true; };
    }, [onCredential, text, locale]);

    if (!CLIENT_ID) return null;
    return <div ref={ref} className="d-flex justify-content-center mt-3" />;
}

// initialize() is idempotent but does real work, so keep one in-flight promise.
let socialLoginInit = null;
function initSocialLogin() {
    if (!socialLoginInit) {
        socialLoginInit = SocialLogin.initialize({
            google: Capacitor.getPlatform() === 'ios'
                ? { iOSClientId: IOS_CLIENT_ID, iOSServerClientId: CLIENT_ID }
                : { webClientId: CLIENT_ID },
        });
    }
    return socialLoginInit;
}

/** Native sign-in button: Android's account picker instead of the GIS iframe. */
function NativeGoogleButton({ onCredential }) {
    const { t } = useTranslation('auth');
    const [busy, setBusy] = useState(false);

    const signIn = async () => {
        setBusy(true);
        try {
            await initSocialLogin();
            const { result } = await SocialLogin.login({ provider: 'google', options: {} });
            if (result?.idToken) onCredential(result.idToken);
            else toast.error(t('googleLoginFailed'));
        } catch (error) {
            // Backing out of the account picker is not a failure worth shouting about.
            if (error?.code !== 'USER_CANCELLED') toast.error(t('googleLoginFailed'));
        } finally {
            setBusy(false);
        }
    };

    if (!CLIENT_ID) return null;
    if (Capacitor.getPlatform() === 'ios' && !IOS_CLIENT_ID) return null;
    return (
        <div className="d-flex justify-content-center mt-3">
            <Button text={t('continueWithGoogle')} onClick={signIn} disabled={busy} />
        </div>
    );
}

/** Picks the sign-in door for the platform. IS_NATIVE never changes at runtime. */
export function GoogleButton(props) {
    return IS_NATIVE ? <NativeGoogleButton {...props} /> : <WebGoogleButton {...props} />;
}

/**
 * Full sign-in/sign-up flow for the auth pages:
 * Google button → token → if the account has no phone yet, ask for it (needed for the bot + Bit).
 */
export default function GoogleAuthFlow({ signupConsent } = {}) {
    const { refreshUser, loadUserData } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation('auth');
    const [needPhone, setNeedPhone] = useState(false);
    // Where to go once sign-in (and the phone step, if any) is done: new accounts start with onboarding.
    const [startPath, setStartPath] = useState('/');
    const [phone, setPhone] = useState('');
    const [busy, setBusy] = useState(false);
    const [pendingCredential, setPendingCredential] = useState(null);

    const submitCredential = useCallback(async (credential, consent) => {
        try {
            const result = await userService.googleLogin(credential, peekPendingReferral(), consent);
            clearPendingReferral(); // only after a successful sign-in — see utils/referral.js
            setPendingCredential(null);
            const start = result.isNew ? postSignupPath() : '/';
            if (result.needsPhone) {
                setStartPath(start);
                setNeedPhone(true); // token saved; complete profile before entering
            } else {
                refreshUser();
                navigate(start);
            }
        } catch (error) {
            if (error.response?.data?.code === 'SIGNUP_CONSENT_REQUIRED') {
                setPendingCredential(credential);
                return;
            }
            toast.error(error.response?.data?.error || t('googleLoginFailed'));
        }
    }, [navigate, refreshUser, t]);

    const handleCredential = useCallback(async (credential) => {
        if (signupConsent && !hasSignupConsent(signupConsent)) {
            toast.error(t('consentRequired'));
            return;
        }
        await submitCredential(credential, signupConsent);
    }, [signupConsent, submitCredential, t]);

    const skipPhone = () => {
        refreshUser();
        navigate(startPath);
    };

    const savePhone = async () => {
        const cleaned = phone.replace(/\D/g, '');
        if (!/^[0-9]{7,15}$/.test(cleaned)) return toast.error(t('phoneInvalid'));
        setBusy(true);
        try {
            await userService.updateMe({ phone: cleaned });
            await loadUserData();
            refreshUser();
            navigate(startPath);
        } catch (error) {
            toast.error(error.response?.data?.error || t('phoneSaveFailed'));
        } finally {
            setBusy(false);
        }
    };

    if (!CLIENT_ID) return null;
    if (Capacitor.getPlatform() === 'ios' && !IOS_CLIENT_ID) return null;

    return (
        <>
            <div className="d-flex align-items-center gap-2 mt-3" aria-hidden="true">
                <hr className="flex-grow-1 m-0" style={{ borderColor: 'var(--color-border)' }} />
                <span className="text-muted-ms" style={{ fontSize: '0.85rem' }}>{t('or')}</span>
                <hr className="flex-grow-1 m-0" style={{ borderColor: 'var(--color-border)' }} />
            </div>
            <GoogleButton onCredential={handleCredential} />
            <SocialSignupConsentSheet
                key={pendingCredential ? 'google-consent' : 'google-consent-closed'}
                open={Boolean(pendingCredential)}
                onClose={() => setPendingCredential(null)}
                onConfirm={(consent) => submitCredential(pendingCredential, consent)}
            />

            {/* Skippable: requiring a phone here would gate the whole app on a
                detail only the bot and Bit links need — App Review 5.1.1(v). */}
            <Sheet open={needPhone} onClose={skipPhone} title={t('oneMoreDetail')}>
                <p className="text-muted-ms">
                    {t('phoneForBot')}
                </p>
                <Input
                    label={t('phone')}
                    name="googlePhone"
                    type="tel"
                    inputMode="numeric"
                    placeholder=""
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    required
                />
                <Button text={t('saveAndContinue')} onClick={savePhone} disabled={busy} />
                <button type="button" className="btn-ms btn-ms-link btn-ms-block mt-2" onClick={skipPhone}>
                    {t('skipForNow')}
                </button>
            </Sheet>
        </>
    );
}
